import React, { useState } from 'react';
import Workspace from '../../components/Workspace/Workspace';
import { MaxoptraNotificationResult } from '../../graphql/entities';
import { useWorkspace } from '../../graphql/hooks/useWorkspace';
import { Direction, IMaxoptraNotificationResult, ISalesOrderLine, Maybe } from '../../graphql/schema/p4types';
import { Paths } from '../../@types/keyOfNested';
import { IFormatterProps, IWorkspaceColumnOverride } from '../../@types/workspaceTypes';
import { ModalFormatter } from '../../components/grid-addons/formatters/ModalFormatter';
import {
  makeStyles,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Text,
  CounterBadge,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
} from '@fluentui/react-components';
import { Pill, PillContainer, PillType } from '../../components/Pill/Pill';
import { p4Tokens } from '../../styles/themes/default/p4FluentTheme';

const LazySalesOrderForm: any = React.lazy(() => import('../sales/salesOrderForm/SalesOrderForm'));

const useStyles = makeStyles({
  cell: {
    width: '100%',
    whiteSpace: 'pre-wrap',
  },
  fixedWidthPill: {
    width: '100px',
    justifyContent: 'space-between',
    borderRadius: '3px',
  },
  statusContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    gap: '5px',
  },
  popoverContent: {
    maxWidth: '600px',
    maxHeight: '400px',
    overflow: 'auto',
  },
  productTable: {
    width: '100%',
  },
  tableHeader: {
    borderBottomWidth: '1px',
    borderBottomColor: p4Tokens.colorNeutralForeground4,
    borderBottomStyle: 'solid',
    overflow: 'hidden',
    fontWeight: 'bold',
    fontSize: '16px',
    marginBottom: '8px',
    paddingBottom: '8px',
  },
});

const StatusFormatter = (props: IFormatterProps<unknown>) => {
  const styles = useStyles();
  const rowData = props.dependentValues.rowData as IMaxoptraNotificationResult;

  const failingGroupCodeValidationCount = rowData.linesFailingGroupCodeValidation?.length ?? 0;
  const notifiedForUpdateCount = rowData.linesNotifiedForUpdate?.length ?? 0;
  const notifiedForCreateCount = rowData.linesNotifiedForCreate?.length ?? 0;
  const lockedDeliveryCount = rowData.linesInLockedDelivery?.length ?? 0;

  const ProductInfoTable = ({
    lines,
    expandedLabel,
    showGroupCode,
  }: {
    lines: Array<Maybe<ISalesOrderLine>>;
    expandedLabel?: string;
    showGroupCode?: boolean;
  }) => (
    <>
      {expandedLabel && (
        <Text className={styles.tableHeader} block>
          {expandedLabel}
        </Text>
      )}
      <Table className={styles.productTable}>
        <TableHeader>
          <TableRow>
            <TableHeaderCell>Product Reference</TableHeaderCell>
            <TableHeaderCell>Description</TableHeaderCell>
            {showGroupCode && <TableHeaderCell>Dispatch Group Code</TableHeaderCell>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {lines.map((line) => (
            <TableRow key={`${line?.id}`}>
              <TableCell>{line?.product?.reference}</TableCell>
              <TableCell>{line?.product?.description}</TableCell>
              {showGroupCode && <TableCell>{line?.dispatchGroupCode}</TableCell>}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );

  const CounterBadgeWrapper = ({ count }: { count: number }) => (
    <CounterBadge appearance="filled" color="informative" size="small" count={count} />
  );

  const PillWithPopover = ({
    type,
    label,
    expandedLabel,
    count,
    lines,
    showGroupCode,
  }: {
    type: PillType;
    label: string;
    expandedLabel?: string;
    count?: number;
    lines?: Array<Maybe<ISalesOrderLine>>;
    showGroupCode?: boolean;
  }) => {
    const [open, setOpen] = useState(false);

    return (
      <Popover
        open={open && lines && lines.length > 0}
        onOpenChange={(e, data) => setOpen(data.open)}
        openOnHover
        mouseLeaveDelay={0}
      >
        <PopoverTrigger>
          <Pill type={type} className={styles.fixedWidthPill}>
            {label}
            {count && count > 0 && <CounterBadgeWrapper count={count} />}
          </Pill>
        </PopoverTrigger>
        <PopoverSurface className={styles.popoverContent}>
          {open && <ProductInfoTable lines={lines} expandedLabel={expandedLabel} showGroupCode={showGroupCode} />}
        </PopoverSurface>
      </Popover>
    );
  };

  return (
    <PillContainer className={styles.statusContainer}>
      {notifiedForUpdateCount > 0 && (
        <PillWithPopover
          type={PillType.Success}
          label="Updated"
          expandedLabel="Order Lines Updated"
          count={notifiedForUpdateCount}
          lines={rowData.linesNotifiedForUpdate || []}
        />
      )}

      {notifiedForCreateCount > 0 && (
        <PillWithPopover
          type={PillType.Success}
          label="Created"
          expandedLabel="Order Lines Created"
          count={notifiedForCreateCount}
          lines={rowData.linesNotifiedForCreate || []}
        />
      )}

      {failingGroupCodeValidationCount > 0 && (
        <PillWithPopover
          type={PillType.Warning}
          label="Group Code"
          expandedLabel="Order Lines Failing Group Code Validation"
          count={failingGroupCodeValidationCount}
          lines={rowData.linesFailingGroupCodeValidation || []}
          showGroupCode={true}
        />
      )}

      {lockedDeliveryCount > 0 && (
        <PillWithPopover
          type={PillType.Warning}
          label="Locked"
          expandedLabel="Order Lines In Locked Delivery"
          count={lockedDeliveryCount}
          lines={rowData.linesInLockedDelivery || []}
        />
      )}

      {rowData.hasMaxoptraApiError && <PillWithPopover type={PillType.Danger} label="Maxoptra Error" />}
    </PillContainer>
  );
};

const fieldNames: Array<Paths<IMaxoptraNotificationResult>> = [
  'salesOrder.orderNumber',
  'salesOrder.orderReference',
  'salesOrder.id',
  'failureMessage',
];

const columnOverrides: IWorkspaceColumnOverride = {
  'salesOrder.orderNumber': {
    name: 'Sales Order',
    formatter: ModalFormatter,
    width: 125,
    options: {
      modalContent: LazySalesOrderForm,
      modalProps: { forceReadOnly: false },
      entityId: 'salesOrder.id',
    },
  },
  'salesOrder.orderReference': {
    name: 'Sales Order Reference',
    width: 250,
  },
  'salesOrder.id': {
    name: 'Status',
    formatter: StatusFormatter,
    sortable: false,
  },
};

const MaxoptraNotificationResultsWorkspace = (props) => {
  const workspaceProps = useWorkspace<IMaxoptraNotificationResult>(props, MaxoptraNotificationResult, {
    columns: fieldNames,
    initialSortColumn: 'createdAt',
    initialSortDirection: Direction.Desc,
    columnOverrides: columnOverrides,
  });

  return (
    <Workspace
      entityType={MaxoptraNotificationResult.type}
      title={props.title || MaxoptraNotificationResult.title}
      formUrl={MaxoptraNotificationResult.formUrl}
      defaultQuery={MaxoptraNotificationResult.workspace}
      {...workspaceProps}
      {...props}
      entity={MaxoptraNotificationResult}
      workspaceActionButtonsHOC={() => null} // remove actions button (just want to show close)
    />
  );
};

export default MaxoptraNotificationResultsWorkspace;
